/**
 * FeedItemCard — one card in the Feed (B-WP11b + nod B-WP11c).
 *
 * Friends lane: "@handle cooked <title>" + rating (the shared `X.X★` readout —
 * design.md §6.8; one decimal per Decision B4)
 * + note excerpt or message + the cook/delivery time line (late shares read
 * "cooked Thu · shared today" — B-WP1b's exact rule via formatCookDatePair) + the
 * NOD affordance and nod line (B-WP11c).
 * Following lane: "@handle published <title>" linking to /r/:uuid. NO nod (nods
 * attach to cooks, never publish events — decision F2).
 *
 * The card NEVER exposes a body. The title links to /r/:recipeUuid ONLY when the
 * recipe is public (`publicUuid` non-null); a friends-shared cook of a private
 * recipe renders its title as plain text (the body-never-leaks contract).
 *
 * Nod is quiet + factual — "Nod" (never a filled-heart icon, never a numeral).
 * The nod line names up to three handles then "and others" — words, never a
 * count (vision pillar 3). Copy: branding.md §5 (no exclamations, no banned words).
 */

import { useCallback, useEffect, useState } from "react";
import { Rating } from "@proof/ui/recipe";
import { formatHandle, formatCookDatePair } from "~/features/groups/time";
import { toggleNod as toggleNodApi, loadNodHandles as loadNodHandlesApi } from "./api";
import type { FeedItem } from "./api";

/** The follow-lane verb for each event kind (quiet, factual — no exclamations). */
function eventVerb(kind: FeedItem["eventKind"]): string {
  switch (kind) {
    case "spinoff_published":
      return "shared a spin-off";
    case "go_to_updated":
      return "updated";
    case "recipe_published":
    default:
      return "published";
  }
}

/**
 * Render the nod line as WORDS, never a numeral: up to three handles, then "and
 * others". Empty → null (no line). Examples:
 *   [anna]                     → "@anna nodded"
 *   [anna, bob]                → "@anna and @bob nodded"
 *   [anna, bob, cara]          → "@anna, @bob and @cara nodded"
 *   [anna, bob, cara, dan, …]  → "@anna, @bob, @cara and others nodded"
 */
export function formatNodLine(handles: string[]): string | null {
  if (handles.length === 0) return null;
  const at = (h: string) => formatHandle(h);
  if (handles.length === 1) return `${at(handles[0]!)} nodded`;
  if (handles.length === 2) return `${at(handles[0]!)} and ${at(handles[1]!)} nodded`;
  if (handles.length === 3)
    return `${at(handles[0]!)}, ${at(handles[1]!)} and ${at(handles[2]!)} nodded`;
  return `${at(handles[0]!)}, ${at(handles[1]!)}, ${at(handles[2]!)} and others nodded`;
}

export interface FeedItemCardProps {
  item: FeedItem;
  now?: number;
  /** The signed-in viewer's handle — derives the initial pressed state. */
  viewerHandle?: string;
  /** Injectable loaders for tests (default to the real oRPC client). */
  loadNods?: (attemptUuid: string) => Promise<string[]>;
  onToggleNod?: (attemptUuid: string, on: boolean) => Promise<void>;
  /**
   * Preview mode — the dashboard "From friends" strip. A glanceable summary: the
   * Nod affordance is OMITTED so the card does NOT fire the eager `feed.nods.list`
   * read on mount. Without this a 12-card strip would issue up to 12 nod reads on
   * every /app landing (a perf regression on the main route). Full nod
   * interaction stays on /app/feed. Defaults to false, so the /app/feed card is
   * byte-identical (eager nod read + interactive Nod preserved).
   */
  preview?: boolean;
}

export function FeedItemCard({
  item,
  now,
  viewerHandle,
  loadNods = loadNodHandlesApi,
  onToggleNod = toggleNodApi,
  preview = false,
}: FeedItemCardProps) {
  const nowMs = now ?? Date.now();
  const title =
    item.publicUuid !== null ? (
      <a className="proof-feed-card__title" href={`/r/${encodeURIComponent(item.publicUuid)}`}>
        {item.recipeTitle}
      </a>
    ) : (
      <span className="proof-feed-card__title">{item.recipeTitle}</span>
    );

  if (item.lane === "following") {
    return (
      <article className="proof-feed-card" data-testid="feed-card" data-lane="following">
        <div className="proof-feed-card__head">
          <span className="proof-feed-card__handle">{formatHandle(item.ownerHandle)}</span>
          <span className="proof-feed-card__verb">{eventVerb(item.eventKind)}</span>
          {title}
        </div>
      </article>
    );
  }

  // Friends lane.
  const datePair =
    item.cookedAt !== null && item.deliveredAt !== null
      ? formatCookDatePair(item.cookedAt, item.deliveredAt, nowMs)
      : null;

  return (
    <article className="proof-feed-card" data-testid="feed-card" data-lane="friends">
      <div className="proof-feed-card__head">
        <span className="proof-feed-card__handle">{formatHandle(item.ownerHandle)}</span>
        <span className="proof-feed-card__verb">cooked</span>
        {title}
      </div>
      {item.rating !== null && (
        <div className="proof-feed-card__rating">
          {/* The shared `X.X★` readout (design.md §6.8) — was a bare `5.0/5`
              with no star, the only rating on the product that read as a
              fraction. Muted tone: the figure is card metadata, not its
              headline; the star keeps the accent. */}
          <Rating value={item.rating} tone="muted" fontSize="13px" testId="feed-card-rating" />
        </div>
      )}
      {item.message && <div className="proof-feed-card__message">{item.message}</div>}
      {!item.message && item.noteExcerpt && (
        <div className="proof-feed-card__excerpt">{item.noteExcerpt}</div>
      )}
      {datePair && (
        <div className="proof-feed-card__time" data-testid="feed-card-time">
          {datePair.cooked}
          {datePair.shared ? ` · ${datePair.shared}` : ""}
        </div>
      )}
      {!preview && (
        <NodControls
          attemptUuid={item.id}
          {...(viewerHandle !== undefined ? { viewerHandle } : {})}
          loadNods={loadNods}
          onToggleNod={onToggleNod}
        />
      )}
    </article>
  );
}

/**
 * The nod affordance + nod line for a friends-lane card. Loads the nod handles on
 * mount (owner or holder read); derives the pressed state from the viewer's
 * handle when known. Optimistic toggle with a Rule-8 revert + inline error.
 */
function NodControls({
  attemptUuid,
  viewerHandle,
  loadNods,
  onToggleNod,
}: {
  attemptUuid: string;
  viewerHandle?: string;
  loadNods: (attemptUuid: string) => Promise<string[]>;
  onToggleNod: (attemptUuid: string, on: boolean) => Promise<void>;
}) {
  const [handles, setHandles] = useState<string[]>([]);
  const [pressed, setPressed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadNods(attemptUuid)
      .then((hs) => {
        if (cancelled) return;
        setHandles(hs);
        if (viewerHandle) setPressed(hs.includes(viewerHandle));
      })
      .catch((err: unknown) => {
        // A read failure degrades to no nod line — not a mutate, so no toast.
        console.debug({ event: "feed_nods_load_failed", attemptUuid, err });
      });
    return () => {
      cancelled = true;
    };
  }, [attemptUuid, viewerHandle, loadNods]);

  const handleToggle = useCallback(async () => {
    if (busy) return;
    const next = !pressed;
    // Optimistic: flip pressed + adjust the local handle list when we know who
    // the viewer is (so the line updates immediately).
    setPressed(next);
    setError(null);
    if (viewerHandle) {
      setHandles((hs) => (next ? [...hs, viewerHandle] : hs.filter((h) => h !== viewerHandle)));
    }
    setBusy(true);
    try {
      await onToggleNod(attemptUuid, next);
    } catch (err) {
      // Rule 8: revert the optimistic state and surface a visible error.
      console.error({ event: "feed_nod_toggle_failed", attemptUuid, err });
      setPressed(!next);
      if (viewerHandle) {
        setHandles((hs) => (next ? hs.filter((h) => h !== viewerHandle) : [...hs, viewerHandle]));
      }
      setError("Couldn’t save the nod.");
    } finally {
      setBusy(false);
    }
  }, [attemptUuid, busy, pressed, viewerHandle, onToggleNod]);

  const nodLine = formatNodLine(handles);

  return (
    <div className="proof-feed-card__nod">
      <button
        type="button"
        className="proof-feed-card__nod-btn"
        aria-pressed={pressed}
        disabled={busy}
        onClick={() => void handleToggle()}
        data-testid="feed-nod"
      >
        Nod
      </button>
      {nodLine && (
        <span className="proof-feed-card__nod-line" data-testid="feed-nod-line">
          {nodLine}
        </span>
      )}
      {error && (
        <span role="alert" className="proof-feed-card__nod-error" data-testid="feed-nod-error">
          {error}
        </span>
      )}
    </div>
  );
}
